/** PURE. Is this build being served by the staging deployment?
 *
 *  ⚠️ HOSTNAME, NOT A BUILD VARIABLE, AND DELIBERATELY SO. The obvious
 *  alternative is a `VITE_*` flag set by `web-staging.yml`, which is how the
 *  PostHog key separates the environments. It is the wrong tool here because
 *  the two failure directions are not symmetric: a missing flag on staging
 *  costs a banner, while a flag that leaks into the production build tells
 *  every referee in the region that the live site is a test. A hostname cannot
 *  leak — `referee.ayso13.org` does not contain the suffix — and it needs no
 *  workflow change, so there is nothing to forget when a surface is added.
 *
 *  ⚠️ LIVES HERE, NOT IN ONE SPA, because the rule is the platform's and every
 *  surface shares the suffix. The first copy sat in referee-spa's lib, which
 *  meant the second surface wanting the banner would have copied the file and
 *  the literal with it.
 *
 *  ⚠️ MATCHES THE LABEL ONLY, NEVER GATES BEHAVIOR. Nothing may read this to
 *  decide what to write, what to send, or whom to mail: staging's safety comes
 *  from `ENVIRONMENT` on the Worker (`inleagueWrite`, `NOTIFY_TEST_RECIPIENT`),
 *  which is server-side and unspoofable. A client-side hostname test is a
 *  convenience for the person reading the page and nothing more. */
const STAGING_SUFFIX = '.staging.ayso13.org'

export function isStagingHost(hostname: string): boolean {
  return hostname.toLowerCase().endsWith(STAGING_SUFFIX)
}

/** PURE. The live host a staging host stands in for (`referee.staging.ayso13.org`
 *  → `referee.ayso13.org`), or null off staging. The staging banner links it so
 *  a volunteer who lands on staging by mistake has somewhere to go. */
export function productionHost(hostname: string): string | null {
  const lower = hostname.toLowerCase()
  return lower.endsWith(STAGING_SUFFIX) ? `${lower.slice(0, -STAGING_SUFFIX.length)}.ayso13.org` : null
}

/** PURE. The staging host for an app (`'stats'` → `stats.staging.ayso13.org`).
 *  Every place that builds a staging host calls this, so the shape lives in one
 *  constant. ⚠️ The leading dot in the suffix is what keeps `staging.ayso13.org`,
 *  the www site's staging, from counting as one of ours. */
export function stagingHost(app: string): string {
  return `${app}${STAGING_SUFFIX}`
}

/** The staging banner's second line, up to the live-site link (which follows,
 *  then " instead."). One copy, because the SPA banner and the stats Worker's
 *  `/submit` page both print it. It says nothing about signing in alone: the
 *  banner also sits on the public report forms, which have no sign-in. */
export const STAGING_NOTICE_LEAD = 'This is a test copy of the site. Its emails, sign-in codes included, never reach anyone. Use'
